import { fetchWithClerkJwt } from "./fetch-with-clerk";

/**
 * POS-transaction trends — the dispensary's own store history from their POS,
 * which is a different source from the orders TheDrop fulfills. Everything here
 * is already aggregated server-side in store-local time; the UI only formats.
 */

export type PosTrendsHour = {
  hour: number;
  deliveries: number;
  transactions: number;
  deliveryRevenueCents: number;
};

export type PosTrendsDayOfWeek = {
  dow: number;
  label: string;
  deliveries: number;
  transactions: number;
  deliveryRevenueCents: number;
  deliveriesPerDay: number;
};

export type PosTrendsDay = {
  date: string;
  deliveries: number;
  transactions: number;
  deliveryRevenueCents: number;
};

export type PosTrendsPeak = {
  hour: number | null;
  deliveries: number;
  pctOfDeliveries: number;
  blockStartHour: number | null;
  blockHours: number;
  blockDeliveries: number;
  blockPctOfDeliveries: number;
};

export type PosTrendsTotals = {
  transactions: number;
  deliveries: number;
  nonDeliveries: number;
  deliveryPct: number;
  totalRevenueCents: number;
  deliveryRevenueCents: number;
  deliveryRevenuePct: number;
  avgDeliveryTicketCents: number;
  avgNonDeliveryTicketCents: number;
  activeDays: number;
  deliveriesPerDay: number;
};

export type PosTrendsToday = {
  date: string;
  transactions: number;
  deliveries: number;
  deliveryRevenueCents: number;
};

/**
 * What the warehouse holds about a delivery — deliberately thin. The reporting
 * feed keeps only an opaque POS customer id and no PII; name, address, items
 * and live status arrive only once `/Deliveries` ingestion is live.
 */
export type PosTrendsRecentDelivery = {
  posTransactionId: string;
  atIso: string;
  orderSource: string | null;
  totalCents: number;
};

export type PosTransactionTrends = {
  window: { days: number; fromIso: string; toIso: string; timeZone: string };
  totals: PosTrendsTotals;
  byHour: PosTrendsHour[];
  byDayOfWeek: PosTrendsDayOfWeek[];
  byDay: PosTrendsDay[];
  peak: PosTrendsPeak;
  /** Null when the API predates the today/recentDeliveries fields (deploy skew, cached payload). */
  today: PosTrendsToday | null;
  recentDeliveries: PosTrendsRecentDelivery[];
};

export async function fetchDispensaryPosTrends(
  getToken: () => Promise<string | null>,
  dispensaryId: string,
  days: number,
): Promise<PosTransactionTrends | null> {
  const res = await fetchWithClerkJwt(
    getToken,
    `/api/v1/dispensaries/${dispensaryId}/pos-transactions/trends?days=${days}`,
  );
  if (!res.ok) return null;
  return normalizePosTrendsPayload(await res.json());
}

/**
 * The web app and the API deploy independently (Vercel vs Railway), so a
 * freshly deployed client can receive a payload from an API that predates the
 * `today` / `recentDeliveries` fields — as can a cached query from a tab that
 * was open across the deploy. Rendering must degrade (hide the Today strip,
 * empty POS tab), never throw. Exported for tests.
 */
export function normalizePosTrendsPayload(body: unknown): PosTransactionTrends {
  const raw = (body ?? {}) as Record<string, unknown>;
  return {
    ...(raw as unknown as PosTransactionTrends),
    today: (raw.today ?? null) as PosTrendsToday | null,
    recentDeliveries: Array.isArray(raw.recentDeliveries)
      ? (raw.recentDeliveries as PosTrendsRecentDelivery[])
      : [],
  };
}

/** `16` → `4 PM`. Hours are already store-local, so no conversion here. */
export function formatHourLabel(hour: number): string {
  const suffix = hour < 12 ? "AM" : "PM";
  const base = hour % 12 === 0 ? 12 : hour % 12;
  return `${base} ${suffix}`;
}

/** `16` → `4–5 PM`, the way a staffing window is actually spoken. */
export function formatHourRange(startHour: number, hours = 1): string {
  const end = (startHour + hours) % 24;
  const startSuffix = startHour < 12 ? "AM" : "PM";
  const endSuffix = end < 12 ? "AM" : "PM";
  const s = startHour % 12 === 0 ? 12 : startHour % 12;
  const e = end % 12 === 0 ? 12 : end % 12;
  return startSuffix === endSuffix ? `${s}–${e} ${endSuffix}` : `${s} ${startSuffix} – ${e} ${endSuffix}`;
}

export function formatCents(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString("en-US")}`;
}

/** Compact money for tight stat tiles: `$1,234,500` → `$12.3k` (dollars). */
export function formatCentsCompact(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(1)}k`;
  return `$${Math.round(dollars)}`;
}

/** `2026-08-24T15:54:00Z` → `Aug 24, 9:54 AM` in the store's zone. */
export function formatStoreDateTime(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));
}
